import {
  normalizeDeploymentKind,
  type Deployment,
} from "./deployments";
import type { Organization } from "./organizations";
import {
  preferSessionName,
  type Space,
  sessionApplicationId,
  type SpaceListedSession,
} from "./spaces";
import { apiClient } from "./client";
import { getConsoleApiUrl } from "@/config/runtime";
import { getAuthToken } from "@/services/hankoAuth";
import { readInstanceRole } from "@/utils/instanceRole";

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  photoUrl?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  language?: string;
  authent?: string;
  role?: string;
}

export interface UserBootstrapResponse {
  is_enrolled: boolean;
  profile_completed?: boolean;
  requires_organization?: boolean;
  has_organization?: boolean;
}

function normalizeBootstrapResponse(
  response: unknown,
): UserBootstrapResponse {
  const candidate =
    response && typeof response === "object" && "data" in response
      ? (response as { data?: unknown }).data
      : response;
  const value =
    candidate && typeof candidate === "object"
      ? (candidate as Record<string, unknown>)
      : {};

  return {
    is_enrolled: value.is_enrolled === true,
    profile_completed:
      typeof value.profile_completed === "boolean"
        ? value.profile_completed
        : undefined,
    requires_organization:
      typeof value.requires_organization === "boolean"
        ? value.requires_organization
        : undefined,
    has_organization:
      typeof value.has_organization === "boolean"
        ? value.has_organization
        : undefined,
  };
}

export interface UserEnrollmentInput {
  username: string;
  first_name: string;
  last_name: string;
  language: string;
  organization_name?: string;
  is_personal_organization: boolean;
}

export interface ConsoleProfileResponse {
  profile: {
    id: string;
    email: string;
    display_name?: string;
    photo_url?: string;
    username?: string;
    first_name?: string;
    last_name?: string;
    language?: string;
    profile_completed?: boolean;
    updated_at?: string;
  };
}

export interface UserMembership {
  organization: Organization;
  role: string;
  joinedAt: string;
  deployments: Deployment[];
  spaces: Space[];
}

const mapOrganization = (org: Record<string, any>): Organization => {
  const planFeaturesObject =
    org?.plan_features && typeof org.plan_features === "object"
      ? org.plan_features
      : null;
  const resolvedFeatures = Array.isArray(org?.features)
    ? org.features
    : planFeaturesObject
      ? Object.entries(planFeaturesObject)
          .filter(
            (entry): entry is [string, boolean] =>
              typeof entry[0] === "string" && Boolean(entry[1]),
          )
          .map(([key]) => key)
      : undefined;

  return {
    id: String(org?.id ?? ""),
    name: org?.name ?? "Organization",
    slug: org?.slug ?? org?.name?.toLowerCase?.().replace(/\s+/g, "-") ?? "",
    status: org?.status ?? "active",
    createdAt: org?.created_at ?? org?.createdAt ?? new Date().toISOString(),
    updatedAt: org?.updated_at ?? org?.updatedAt ?? new Date().toISOString(),
    plan: org?.plan ?? org?.plan_name ?? org?.plan_code,
    features: resolvedFeatures,
    metadata: {
      ...(org?.metadata && typeof org.metadata === "object"
        ? org.metadata
        : {}),
      plan_code: org?.plan_code,
      plan_name: org?.plan_name,
      plan_limits:
        org?.plan_limits && typeof org.plan_limits === "object"
          ? org.plan_limits
          : undefined,
    },
  };
};

const mapDeployment = (deployment: Record<string, any>): Deployment => ({
  id: String(deployment?.id ?? deployment?.deployment_id ?? ""),
  name: deployment?.name ?? deployment?.deployment_name ?? "Agent",
  orgId: String(
    deployment?.organization_id ??
      deployment?.organizationId ??
      deployment?.orgId ??
      "",
  ),
  status: deployment?.status ?? "active",
  version: deployment?.version ?? deployment?.deployment_version ?? "0.0.0",
  recipes: [],
  createdAt:
    deployment?.created_at ?? deployment?.createdAt ?? new Date().toISOString(),
  updatedAt:
    deployment?.updated_at ?? deployment?.updatedAt ?? new Date().toISOString(),
  description: deployment?.description,
  environment: deployment?.environment,
  deploymentKind: normalizeDeploymentKind(
    deployment?.deployment_kind ?? deployment?.deploymentKind,
  ),
  isAdmin: deployment?.is_admin ?? deployment?.isAdmin,
  instanceRole: readInstanceRole(
    deployment?.instance_role ?? deployment?.instanceRole,
  ),
});

const mapSpace = (space: Record<string, any>): Space => {
  const edgeDeployments = Array.isArray(space?.edges?.deployment)
    ? space.edges.deployment
    : Array.isArray(space?.deployment)
      ? space.deployment
      : [];
  const deploymentIds = edgeDeployments
    .map((item: any) =>
      String(item?.id ?? item?.deployment_id ?? item?.deploymentId ?? ""),
    )
    .filter(Boolean);
  const directDeploymentId = String(
    space?.deployment_id ??
      space?.deploymentId ??
      space?.target_deployment_id ??
      space?.targetDeploymentId ??
      "",
  );

  const rawSessions = Array.isArray(space?.sessions)
    ? space.sessions
    : Array.isArray(space?.edges?.sessions)
      ? space.edges.sessions
      : [];
  const sessions = rawSessions
    .map(
      (session: any): SpaceListedSession => ({
        ...(sessionApplicationId(session)
          ? { applicationId: sessionApplicationId(session) }
          : {}),
        id: String(session?.id ?? session?.session_id ?? ""),
        name: preferSessionName(session?.name, session?.title) || "Session",
        createdAt:
          session?.created_at ??
          session?.createdAt ??
          new Date().toISOString(),
        updatedAt:
          session?.updated_at ??
          session?.updatedAt ??
          new Date().toISOString(),
        lastActivityAt:
          session?.last_activity_at ??
          session?.lastActivityAt ??
          session?.updated_at ??
          session?.updatedAt ??
          session?.created_at ??
          session?.createdAt ??
          new Date().toISOString(),
        status: session?.status ?? "active",
      }),
    )
    .filter((session: SpaceListedSession) => Boolean(session.id));

  return {
    id: String(space?.id ?? ""),
    name: space?.name ?? "Workspace",
    description: space?.description ?? "",
    orgId: String(
      space?.organization_id ?? space?.organizationId ?? space?.orgId ?? "",
    ),
    deploymentId: directDeploymentId || deploymentIds[0] || undefined,
    deploymentIds: deploymentIds.length > 0 ? deploymentIds : undefined,
    sessions,
    createdAt:
      space?.created_at ?? space?.createdAt ?? new Date().toISOString(),
    updatedAt:
      space?.updated_at ?? space?.updatedAt ?? new Date().toISOString(),
    status: space?.status ?? "active",
    // Listed for an invitation into some of its discussions (AGENT-862).
    access: space?.access === "session" ? "session" : undefined,
  };
};

const mapProfile = (profile: Record<string, any>): UserProfile => ({
  id: String(profile?.id ?? ""),
  email: profile?.email ?? "",
  displayName:
    profile?.displayName ??
    profile?.display_name ??
    profile?.name ??
    profile?.username,
  photoUrl:
    profile?.photoUrl ??
    profile?.photo_url ??
    profile?.avatar ??
    profile?.picture,
  username: profile?.username ?? undefined,
  firstName: profile?.first_name ?? profile?.firstName ?? undefined,
  lastName: profile?.last_name ?? profile?.lastName ?? undefined,
  language: profile?.language ?? undefined,
  authent: profile?.authent ?? undefined,
  role: profile?.role ?? undefined,
});

const mapMembership = (membership: Record<string, any>): UserMembership => ({
  organization: mapOrganization(membership?.organization ?? {}),
  role: membership?.role ?? "member",
  joinedAt:
    membership?.joined_at ?? membership?.joinedAt ?? new Date().toISOString(),
  deployments: Array.isArray(membership?.deployments)
    ? membership.deployments.map((item: any) => mapDeployment(item ?? {}))
    : [],
  spaces: Array.isArray(membership?.spaces)
    ? membership.spaces.map((item: any) => mapSpace(item ?? {}))
    : [],
});

export const userAPI = {
  async provision(): Promise<{
    message?: string;
    enrollment_method?: string;
    user?: Record<string, any>;
  }> {
    return apiClient.post("/user/provision");
  },

  async bootstrap(): Promise<UserBootstrapResponse> {
    const response = await apiClient.post<
      UserBootstrapResponse | { data?: UserBootstrapResponse } | undefined
    >("/user/bootstrap");
    return normalizeBootstrapResponse(response);
  },

  async enroll(input: UserEnrollmentInput): Promise<{
    message?: string;
    user?: Record<string, any>;
    organization?: Record<string, any>;
  }> {
    return apiClient.post("/user/enroll", input);
  },

  async updateConsoleProfile(input: UserEnrollmentInput): Promise<ConsoleProfileResponse> {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Console profile update requires Hanko authentication");
    }
    const response = await fetch(`${getConsoleApiUrl()}/api/v1/user/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        username: input.username,
        first_name: input.first_name,
        last_name: input.last_name,
        language: input.language,
      }),
    });
    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(body || `Console profile update failed with status ${response.status}`);
    }
    return (await response.json()) as ConsoleProfileResponse;
  },

  async getCurrentUser(): Promise<{
    user: UserProfile | null;
    organizations: UserMembership[];
  }> {
    const response = await apiClient.get<{
      user?: Record<string, any>;
      organizations?: Record<string, any>[];
    }>("/user/me");

    return {
      user: response?.user ? mapProfile(response.user) : null,
      organizations: Array.isArray(response?.organizations)
        ? response.organizations.map((item) => mapMembership(item ?? {}))
        : [],
    };
  },
};
